import type { Client } from '~/types/models';

type ResolveDocumentRecipientInput = {
    clientId?: string;
};

export type ResolvedDocumentRecipient = {
    clientId?: string;
    client: Client | null;
    recipientName?: string;
    recipientEmail?: string;
    recipientType: 'client' | 'unknown';
};

function normalizeText(value: unknown): string {
    return String(value || '').trim();
}

export function resolvePrimaryClientContact(client: Client | null): { name?: string; email?: string } {
    const contacts = Array.isArray(client?.contacts) ? client.contacts : [];
    const primaryContact = contacts.find(contact => contact?.isPrimary && normalizeText(contact.email));
    const fallbackContact = contacts.find(contact => normalizeText(contact?.email));
    const contact = primaryContact || fallbackContact;

    return {
        name: normalizeText(contact?.name) || normalizeText(client?.contactName) || normalizeText(client?.name) || undefined,
        email: normalizeText(contact?.email) || normalizeText(client?.email) || undefined,
    };
}

export async function resolveDocumentRecipient(input: ResolveDocumentRecipientInput): Promise<ResolvedDocumentRecipient> {
    const explicitClientId = normalizeText(input.clientId) || undefined;
    const clientStore = useClientStore();
    let client: Client | null = null;

    if (explicitClientId) {
        client = clientStore.clients.find(entry => entry.id === explicitClientId) || null;
        if (!client?.id) {
            client = await clientStore.fetchClientById(explicitClientId);
        }
    }

    // Client and ClientContact are always the first-class recipient model.
    const clientContact = resolvePrimaryClientContact(client);
    if (normalizeText(clientContact.email)) {
        return {
            clientId: normalizeText(client?.id) || explicitClientId,
            client,
            recipientName: normalizeText(clientContact.name) || normalizeText(client?.name) || undefined,
            recipientEmail: normalizeText(clientContact.email) || undefined,
            recipientType: 'client',
        };
    }

    return {
        clientId: normalizeText(client?.id) || explicitClientId,
        client,
        recipientName: normalizeText(client?.name)
            || normalizeText(clientContact.name)
            || undefined,
        recipientEmail: undefined,
        recipientType: 'unknown',
    };
}

export function clientDisplayName(
    input: Pick<ResolvedDocumentRecipient, 'client'> | { clientName?: string },
): string {
    if ('client' in input) {
        return normalizeText(input.client?.name)
            || 'Unlinked';
    }

    return normalizeText(input.clientName) || 'Unlinked';
}
